import React, {
  Children,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import mergeWith from 'lodash.mergewith';
import { PiArrowLeft, PiArrowRight } from 'react-icons/pi';
import type { Settings as SlickSettings } from 'react-slick';
import styled, { css } from 'styled-components';
// No idea why this isn't found by ESLint?
// eslint-disable-next-line node/no-missing-import
import type { PartialDeep } from 'type-fest';

import {
  Checkbox as BaseCheckbox,
  desktop,
  mobile,
  useBreakpoint,
  useId,
  useProductBrowser,
  useProductFiltersCustomFilters,
} from '@techstyle/react-components';
import { FormattedMessage } from '@techstyle/react-intl';
// eslint-disable-next-line node/no-unpublished-import
import type { CatalogProductFilterDto } from '@techstyle/react-types';

import createContext from '../../utils/createContext';
import { isDeepContained } from '../../utils/helpers';
import { useMeasure } from '../../utils/use-hooks';
import { Component as Carousel } from '../Carousel';

import { useGridFilterBarUtils } from './useGridFilterBarUtils';

/**
 * Grid Filter Bar Context and Root Component
 */

export type PartialCatalogProductFilters = PartialDeep<CatalogProductFilterDto>;

export type EnabledFilters = Record<
  string,
  {
    filter: PartialCatalogProductFilters;
    label: string;
  }
>;

export type FilterActionOptions = {
  id: string;
  filter: PartialCatalogProductFilters;
  label?: string;
};

type PartialCarouselMethods = {
  slickNext: () => void;
  slickPrev: () => void;
};

export type FilterItem = {
  id: string;
  label: string;
  image?: string;
  trackingKey?: string;
  filter: PartialCatalogProductFilters;
};

type ArrowState = {
  nextDisabled: boolean;
  prevDisabled: boolean;
};

type GridFilterBarContextValue = {
  addFilter: (
    options: FilterActionOptions,
    updateQueryParams?: boolean,
    defaultEnabledFilters?: EnabledFilters
  ) => void;
  carouselArrowState: ArrowState;
  carouselCollapsed: boolean;
  carouselRef: React.RefObject<null | PartialCarouselMethods>;
  clearFilters: () => void;
  combinedEnabledFilters: PartialCatalogProductFilters;
  defaultEnabledFilters?: EnabledFilters;
  defaultEnabledFilterLabels: string[];
  enabledFilters: EnabledFilters;
  enabledFilterLabels: string[];
  filterItems?: FilterItem[];
  preventInteractionRef: React.MutableRefObject<boolean>;
  removeFilter: (
    id: string,
    updateQueryParams?: boolean,
    defaultEnabledFilters?: EnabledFilters
  ) => void;
  setCarouselArrowState: (state: ArrowState) => void;
  setDefaultEnabledFilters?: (prev: any) => void;
  setEnabledFilters?: (prev: any) => void;
  toggleFilter: (options: FilterActionOptions) => void;
};

const [useGridFilterBarContext, GridFilterBarContext] =
  createContext<GridFilterBarContextValue>('GridFilterBarContext');

const mergeArrays = (objValue: any[], srcValue: any[]) => {
  if (Array.isArray(objValue)) {
    return Array.from(new Set(objValue.concat(srcValue)));
  }
};

const initialArrowState: ArrowState = {
  nextDisabled: false,
  prevDisabled: false,
};

type FilterBarRootProps = React.PropsWithChildren<{
  filterItems?: FilterItem[];
  carouselCollapsed?: boolean;
}>;

const getEnabledFilters = (
  filterItems: FilterItem[],
  appliedFilters: PartialCatalogProductFilters
) => {
  const enabledFilters: EnabledFilters = {};

  filterItems.forEach(({ label, filter, id }) => {
    const isIncluded = isDeepContained(appliedFilters, filter);
    if (isIncluded) {
      enabledFilters[id] = { label, filter };
    }
  });
  return enabledFilters;
};

/**
 * This component sets up the Filter Bar carousel and provides the context for the
 * GridFilterBarSlide components to add/remove/toggle filters. When filters are modified
 * with the Filter Bar slides, the ProductFilters within the ProductBrowser will be updated.
 */
function GridFilterBarRoot({
  children,
  carouselCollapsed,
  filterItems,
}: FilterBarRootProps) {
  const { appliedFilters, setSelectedFilters } =
    useProductFiltersCustomFilters();
  const [enabledFilters, setEnabledFilters] = useState<EnabledFilters>(() =>
    getEnabledFilters(filterItems, appliedFilters)
  );

  // used only for enabling slides if the id is on the query params.
  // the point is to avoid unnecessary requests that would be triggered if we use enabledFilters instead
  const [defaultEnabledFilters, setDefaultEnabledFilters] =
    useState<EnabledFilters>(() => ({}));
  const [carouselArrowState, setCarouselArrowState] =
    useState(initialArrowState);
  const previousCombinedFilters = useRef<PartialCatalogProductFilters | null>();
  const { baseFilters } = useProductBrowser();
  const carouselRef = useRef(null);
  const preventInteractionRef = useRef<boolean>(false);

  const { addFilter, removeFilter, toggleFilter, clearFilters } =
    useGridFilterBarUtils({
      enabledFilters,
      defaultEnabledFilters,
      setEnabledFilters,
      setDefaultEnabledFilters,
      filterItems,
    });

  const combinedEnabledFilters = useMemo(() => {
    const enabledFiltersArray = Object.values(enabledFilters).map(
      ({ filter }) => filter
    );
    const defaultEnabledFiltersArray = Object.values(defaultEnabledFilters).map(
      ({ filter }) => filter
    );
    if (enabledFiltersArray.length === 0) {
      return null;
    }

    return mergeWith(
      {},
      ...enabledFiltersArray,
      ...defaultEnabledFiltersArray,
      mergeArrays
    ) as PartialCatalogProductFilters;
  }, [defaultEnabledFilters, enabledFilters]);

  const enabledFilterLabels = useMemo(() => {
    const enabledFiltersArray = Object.values(enabledFilters).map(
      ({ label }) => label
    );
    return enabledFiltersArray;
  }, [enabledFilters]);

  // Note: Could be done in the same use memo above, concatenating.
  // But it's probably easier to have these separated
  const defaultEnabledFilterLabels = useMemo(() => {
    const defaultEnabledFiltersArray = Object.values(defaultEnabledFilters).map(
      ({ label }) => label
    );
    return defaultEnabledFiltersArray;
  }, [defaultEnabledFilters]);

  // A filter should be reset if it was previously enabled but is no longer enabled
  // We need to keep track of the previous filters to know which ones to reset.
  // NOTE: While this works for the PoC, we might want to consider a different approach
  // since we can end up losing filters that were applied by the filter sidebar.
  const { filtersToReset, aggregationFiltersToReset } = useMemo(() => {
    if (!previousCombinedFilters.current) {
      previousCombinedFilters.current = combinedEnabledFilters;
      return {
        filtersToReset: [],
        aggregationFiltersToReset: [],
      };
    }
    const { aggregationFilter = {}, ...restFilters } =
      combinedEnabledFilters || {};
    const {
      aggregationFilter: previousAggregationFilter = {},
      ...prevRestFilters
    } = previousCombinedFilters.current;

    previousCombinedFilters.current = combinedEnabledFilters;

    return {
      filtersToReset: Object.keys(prevRestFilters).filter(
        key => !Object.keys(restFilters).includes(key)
      ),
      aggregationFiltersToReset: Object.keys(previousAggregationFilter).filter(
        key => !Object.keys(aggregationFilter).includes(key)
      ),
    };
  }, [combinedEnabledFilters]);

  // This combines the currently applied filters with the enabled filters,
  // and resets any filters that were previously enabled but are no longer enabled.
  // NOTE: If a filter bar item and the filter sidebar are changing the same filter,
  // the filters applied may be lost since we are resetting to the `baseFilters` state.
  const fullFilters = useMemo(() => {
    const newFilters = {
      ...appliedFilters,
      ...combinedEnabledFilters,
      aggregationFilter: {
        ...appliedFilters.aggregationFilter,
        ...combinedEnabledFilters?.aggregationFilter,
      },
    } as PartialCatalogProductFilters;

    if (filtersToReset.length > 0) {
      filtersToReset.forEach(key => {
        newFilters[key] = baseFilters[key];
      });
    }

    if (aggregationFiltersToReset.length > 0) {
      aggregationFiltersToReset.forEach(key => {
        newFilters.aggregationFilter[key] = baseFilters.aggregationFilter[key];
      });
    }

    return newFilters;
  }, [
    aggregationFiltersToReset,
    appliedFilters,
    baseFilters,
    combinedEnabledFilters,
    filtersToReset,
  ]);

  useEffect(() => {
    setSelectedFilters(fullFilters);
  }, [fullFilters, setSelectedFilters]);

  const contextValue = useMemo(
    () => ({
      addFilter,
      carouselRef,
      carouselArrowState,
      carouselCollapsed,
      clearFilters,
      defaultEnabledFilters,
      defaultEnabledFilterLabels,
      filterItems,
      removeFilter,
      setCarouselArrowState,
      setDefaultEnabledFilters,
      setEnabledFilters,
      toggleFilter,
      enabledFilters,
      enabledFilterLabels,
      combinedEnabledFilters,
      preventInteractionRef,
    }),
    [
      addFilter,
      carouselArrowState,
      carouselCollapsed,
      clearFilters,
      defaultEnabledFilters,
      defaultEnabledFilterLabels,
      filterItems,
      removeFilter,
      setDefaultEnabledFilters,
      setEnabledFilters,
      toggleFilter,
      enabledFilters,
      enabledFilterLabels,
      combinedEnabledFilters,
    ]
  );

  return (
    <GridFilterBarContext.Provider value={contextValue}>
      {children}
    </GridFilterBarContext.Provider>
  );
}

/**
 * Grid Filter Bar Carousel and Slide Components
 */

type CollapsedProp = {
  $collapsed: boolean;
};

const CheckboxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const checkmarkStyle = css`
  visibility: visible;
  max-width: 100%;
  height: 100%;
`;

const inputCheckedStyle = css`
  border-color: ${({ theme }) => theme.colors.gunmetal};
  background: ${({ theme }) => theme.colors.gunmetal};
`;

const inputWrapperStyle = css`
  height: 100%;
`;

const inputFocusStyle = css`
  box-shadow: none;
`;

const Checkbox = styled(BaseCheckbox).attrs(() => {
  return {
    checkmarkStyle,
    inputCheckedStyle,
    inputWrapperStyle,
    inputFocusStyle,
  };
})<CollapsedProp>`
  height: 100%;
  border-width: 1px;
  border-color: transparent;
  border-style: solid;

  border-radius: 0px;
  margin: 0;
  overflow: hidden;
  padding: 0;
  ${({ $collapsed }) => {
    return (
      !$collapsed &&
      css`
        ${desktop`
          
          width: 115px;
        `}
        ${mobile`
          width: 100px;
        `}
      `
    );
  }}

  ${desktop`
    border-width: 2px;
    
    border-radius: 0;

    ${({ $collapsed }) =>
      $collapsed &&
      css`
        border-radius: 0;
      `}
  `}

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      padding: 0 8px;

      border-radius: 0;
      background-color: ${({ theme }) => theme.colors.marble};
    `}

  @media (hover: hover) {
    input:hover + && {
      background-color: ${({ theme }) => theme.colors.stone};
      border-color: ${({ theme }) => theme.colors.stone};

      color: ${({ theme }) => theme.colors.black900};

      ${({ $collapsed }) =>
        $collapsed &&
        css`
          background-color: ${({ theme }) => theme.colors.gray200};
          border-color: ${({ theme }) => theme.colors.gray200};
        `}
    }
  }
`;

const LabelWrapper = styled.div`
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Label = styled.label<CollapsedProp>`
  line-height: ${12 / 10};
  text-transform: uppercase;
  padding: 8px;
  text-align: center;

  ${props => props.theme.subHeader.variants.subHeading4Uppercase.textStyles}
  font-weight: 400;

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      ${mobile`
        
        font-weight: 400;
        font-size: 12px;
        line-height: ${14.4 / 12};
      `}
    `}

  ${desktop`
    font-size: 12px;
    
    font-weight: 400;
    letter-spacing: 0.75px;
    line-height: ${14.4 / 12};

    ${({ $collapsed }) =>
      $collapsed &&
      css`
        font-weight: 600;
      `}
  `}
`;

const FilterBarImage = styled.img`
  width: 100%;
  height: auto;
  aspect-ratio: 1/1;
  object-fit: contain;

  background-color: #e4e4e4;
`;

type GridFilterBarSlideProps = {
  id: string;
  label: string;
  image?: string;
  trackingKey?: string;
  filter: PartialCatalogProductFilters;
};

function GridFilterBarSlide({
  id,
  label,
  filter,
  trackingKey,
  image,
}: GridFilterBarSlideProps) {
  const {
    addFilter,
    removeFilter,
    carouselCollapsed: collapsed,
    enabledFilters,
    preventInteractionRef,
    defaultEnabledFilters,
  } = useGridFilterBarContext();
  const enabled = Boolean(enabledFilters[id] || defaultEnabledFilters[id]);
  const checkboxId = useId('filter-bar-checkbox');

  const { isDesktop } = useBreakpoint();
  const showCategoriesInSidebar = !isDesktop;

  const handleClick = useCallback(
    async (event: React.MouseEvent<HTMLInputElement>) => {
      if (preventInteractionRef.current) {
        return;
      }
      const checked = event.currentTarget.checked;

      // When selecting a 'product_category_id_list' with multiple ids, join them in a single string
      // so there's only 1 FilterTag shown. These ids should be split before handling the products request.
      if (
        showCategoriesInSidebar &&
        filter?.aggregationFilter?.product_category_id_list?.length > 0
      ) {
        filter.aggregationFilter.product_category_id_list = [
          filter.aggregationFilter.product_category_id_list.join(','),
        ];
      }
      const updateQueryParams = filter?.type === 'product_category_id_list';

      if (checked) {
        await addFilter(
          { id, filter, label },
          updateQueryParams,
          defaultEnabledFilters
        );
      } else {
        await removeFilter(id, updateQueryParams, defaultEnabledFilters);
      }
    },
    [
      preventInteractionRef,
      showCategoriesInSidebar,
      filter,
      addFilter,
      id,
      label,
      defaultEnabledFilters,
      removeFilter,
    ]
  );

  return (
    <Checkbox
      // @ts-ignore -- Checkbox is not properly typed
      id={checkboxId}
      // Needed to update the key based on the enabled state because the styles
      // were _sometimes_ not updating when the enabled state changed
      key={`${id}-${enabled}`}
      data-filter-bar-tracking-key={trackingKey || label}
      defaultChecked={enabled}
      onClick={handleClick}
      $collapsed={collapsed}
      checkmarkIcon={
        <CheckboxWrapper>
          {!collapsed && image && <FilterBarImage src={image} alt={label} />}
          <LabelWrapper>
            <Label htmlFor={checkboxId} $collapsed={collapsed}>
              {label}
            </Label>
          </LabelWrapper>
        </CheckboxWrapper>
      }
    />
  );
}

const CarouselWrapper = styled.div<CollapsedProp>`
  background-color: #fff;
  .slick-slider {
    ${mobile`
      overflow: hidden;
      margin: 0 -16px;
      padding: 0 12px;
    `}

    .slick-list {
      ${mobile`
      overflow: visible;
    `}
    }

    .slick-track {
      display: flex;
      margin: 0 -4px;
      ${mobile`
        ${({ $collapsed }) =>
          $collapsed &&
          css`
            padding: 8px 0;
          `}
      `}
      ${desktop`
        margin: 0 -8px;
      `}

      .slick-slide {
        display: flex;
        height: auto;
        padding: 0 4px;
        ${desktop`
          padding: 0 8px;
        `}

        .builder-block {
          height: 100%;
        }
      }
    }
  }
`;

const initialCarouselOptions: SlickSettings = {
  variableWidth: true,
  arrows: false,
  dots: false,
};

type CarouselProps = React.PropsWithChildren<{
  carouselOptions?: SlickSettings;
}>;

const GridFilterBarCarousel = ({
  children,
  carouselOptions: carouselOptionsFromProps,
  ...rest
}: CarouselProps) => {
  const {
    carouselRef,
    carouselCollapsed: collapsed,
    filterItems,
    preventInteractionRef,
    setCarouselArrowState,
  } = useGridFilterBarContext();
  const [carouselIndex, setCarouselIndex] = useState(0);
  const { isMobile } = useBreakpoint();
  const [setMeasureRef, wrapperMeasurements] = useMeasure();

  const handleBeforeChange = useCallback(
    (prev: number, next: number) => {
      preventInteractionRef.current = true;

      setCarouselIndex(next);
    },
    [preventInteractionRef]
  );

  const handleAfterChange = useCallback(() => {
    if (preventInteractionRef.current) {
      preventInteractionRef.current = false;
    }
  }, [preventInteractionRef]);

  const carouselOptions = useMemo(() => {
    const slideWidth = isMobile ? 120 : 180;
    const slidesToShow = collapsed
      ? 1
      : Math.floor(wrapperMeasurements?.width / slideWidth) || 1;

    return {
      ...initialCarouselOptions,
      ...carouselOptionsFromProps,
      slidesToShow,
      slidesToScroll: slidesToShow,
      afterChange: handleAfterChange,
      beforeChange: handleBeforeChange,
    };
  }, [
    collapsed,
    carouselOptionsFromProps,
    handleAfterChange,
    handleBeforeChange,
    isMobile,
    wrapperMeasurements,
  ]);

  useEffect(() => {
    if (carouselRef.current) {
      const slidesToShow = carouselOptions.slidesToShow;
      const prevDisabled = carouselIndex === 0;
      const nextDisabled =
        Children.count(children) === carouselIndex + (slidesToShow ?? 0);

      setCarouselArrowState({
        prevDisabled,
        nextDisabled,
      });
    }
  }, [
    carouselIndex,
    carouselOptions,
    carouselRef,
    children,
    setCarouselArrowState,
  ]);

  if (!children && !filterItems) {
    return null;
  }

  return (
    <CarouselWrapper $collapsed={collapsed} ref={setMeasureRef} {...rest}>
      {/* @ts-ignore -- Carousel is not properly typed */}
      <Carousel sliderRef={carouselRef} options={carouselOptions}>
        {children ||
          filterItems.map(item => {
            return <GridFilterBarSlide key={item.id} {...item} id={item.id} />;
          })}
      </Carousel>
    </CarouselWrapper>
  );
};

const ArrowsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const ArrowButton = styled.button`
  height: 24px;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  background-color: transparent
  
  color: #000;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const GridFilterArrows = ({ ...rest }) => {
  const {
    carouselRef,
    carouselArrowState: { nextDisabled, prevDisabled },
    filterItems,
  } = useGridFilterBarContext();

  const handleNextClick = useCallback(() => {
    carouselRef.current?.slickNext();
  }, [carouselRef]);

  const handlePrevClick = useCallback(() => {
    carouselRef.current?.slickPrev();
  }, [carouselRef]);

  // hide arrows if there's no filters at all
  if (!filterItems || !filterItems?.length) {
    return null;
  }

  return (
    <ArrowsWrapper {...rest}>
      <ArrowButton onClick={handlePrevClick} disabled={prevDisabled}>
        <PiArrowLeft size={24} />
      </ArrowButton>
      <ArrowButton onClick={handleNextClick} disabled={nextDisabled}>
        <PiArrowRight size={24} />
      </ArrowButton>
    </ArrowsWrapper>
  );
};

/**
 * Grid Filter Bar Heading
 */

const FilterBarHeadingWrapper = styled.div<{ $isShowingShortTitle: boolean }>`
  // Fixes ellipsis not working for overflow.
  min-width: 0;

  ${({ $isShowingShortTitle }) =>
    $isShowingShortTitle &&
    css`
      margin-top: ${props => props.theme.spacing.small}px;
    `}
`;

const StyledShortTitle = styled.span<{ $isVisible: boolean }>`
  letter-spacing: 0.3px;
  font-size: 12px;
  font-weight: 300;
  line-height: ${14.4 / 12};
  color: ${({ theme }) => theme.colors.gray500};

  ${mobile`
    display: none;
  `}

  ${desktop`
    ${({ $isVisible }) => css`
      visibility: ${$isVisible ? 'visible' : 'hidden'};
    `}
  `}
`;

const StyledTitle = styled.h1`
  ${desktop`
    ${props => props.theme.header.variants.h2.textStyles}
  `}
  ${mobile`
    ${props => props.theme.header.variants.h3.textStyles}
  `}
  text-transform: uppercase;

  // Display ellipsis for long text by setting a max-height and hiding overflow.
  max-height: 29px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  // same ellipsis for ActiveCategoryHeading
  h1 {
    max-height: 29px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export type TitleProps = {
  defaultTitle?: string;
  shortTitle?: string;
};

const GridFilterBarHeading = ({
  defaultTitle = 'All Products',
  shortTitle = 'Products',
}: TitleProps) => {
  const { enabledFilterLabels, defaultEnabledFilterLabels } =
    useGridFilterBarContext();
  const enabledLabels = [...enabledFilterLabels, ...defaultEnabledFilterLabels];
  const [label, ...restLabels] = enabledLabels;
  const isFiltered = Boolean(label);

  return (
    <FilterBarHeadingWrapper
      $isShowingShortTitle={isFiltered && shortTitle?.length > 0}
    >
      <StyledShortTitle $isVisible={isFiltered}>{shortTitle}</StyledShortTitle>
      <StyledTitle>
        {isFiltered ? label : defaultTitle}
        {restLabels.length > 0 ? ` +${restLabels.length}` : ''}
      </StyledTitle>
    </FilterBarHeadingWrapper>
  );
};

/**
 * Grid Filter Bar Clear Button
 */

const StyledClearButton = styled.button`
  color: ${props => props.theme.colors.gunmetal};
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 1.75px;
  line-height: ${29 / 12};
  text-transform: uppercase;
  cursor: pointer;
`;

const ClearButtonWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  ${desktop`
    justify-content: flex-start;
  `}
`;

const GridFilterBarClearButton = () => {
  const { clearFilters, enabledFilters, defaultEnabledFilters } =
    useGridFilterBarContext();
  const isEnabled = useMemo(
    () =>
      Object.keys(enabledFilters).length > 0 ||
      Object.keys(defaultEnabledFilters).length > 0,
    [enabledFilters, defaultEnabledFilters]
  );

  return (
    <ClearButtonWrapper>
      {isEnabled && (
        <StyledClearButton onClick={clearFilters} type="button">
          <FormattedMessage
            defaultMessage="Clear all"
            id="site_product_grid.clear_all"
          />
        </StyledClearButton>
      )}
    </ClearButtonWrapper>
  );
};

export {
  GridFilterBarRoot as Root,
  GridFilterBarSlide as Slide,
  GridFilterBarCarousel as Carousel,
  GridFilterArrows as Arrows,
  GridFilterBarHeading as Heading,
  GridFilterBarClearButton as ClearButton,
  useGridFilterBarContext,
};
